import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from 'styled-components'
import { Typography } from '@chilecompra/react-kit/components'
import Instance from 'highcharts'
import TreemapAddOn from 'highcharts/modules/treemap'
import Highcharts from 'highcharts-react-official'
import { GlobalStyle, GridContainer, GridItem, Nav, NavIcon, NavItem } from './TreemapChart.styles'

TreemapAddOn(Instance)

/**
 * The TreemapChart's component.
 */
const TreemapChart = props => {
  const { data, id, levels, rootIcon, rootName, labelFormatter, onClickMap, onClickTree, onReset } = props
  const theme = useTheme()
  const baseData = useMemo(
    () => [
      { color: theme.palette.charts.navyBlue, id: '1', name: '' },
      { color: theme.palette.charts.cobalt, id: '2', name: '' },
      { color: theme.palette.charts.fauxRoyal, id: '3', name: '' },
      { color: theme.palette.charts.midnight, id: '4', name: '' },
      { color: theme.palette.charts.navyBlue, id: '5', name: '' },
      { color: theme.palette.charts.cobalt, id: '6', name: '' },
      { color: theme.palette.charts.fauxRoyal, id: '7', name: '' },
      { color: theme.palette.charts.midnight, id: '8', name: '' },
      { color: theme.palette.charts.navyBlue, id: '9', name: '' },
      { color: theme.palette.charts.cobalt, id: '10', name: '' }
    ],
    [theme]
  )
  const baseValues = useMemo(() => [15, 8, 6, 5, 4, 4, 3, 3, 2, 2], [])
  const enhancedData = useMemo(
    () => baseData.concat(data).map(datum => ({ ...datum, value: baseValues[datum.index] })),
    [baseData, baseValues, data]
  )
  const handleClickTree = (level, index) => () => onClickTree(level, index)
  const options = {
    accessibility: { enabled: false },
    chart: { type: 'treemap' },
    drilldown: { series: [] },
    plotOptions: { treemap: { cursor: 'pointer' } },
    series: [
      {
        alternateStartingDirection: true,
        background: theme.palette.brand.white,
        data: enhancedData,
        events: {
          click: event => {
            const { categoryId, familyId, label, productId, segmentId, type, level } = event.point

            onClickMap(type, label, categoryId, familyId, productId, segmentId, level)
          }
        },
        layoutAlgorithm: 'strip',
        levels: [
          { borderColor: 'none', borderWidth: 0, level: 1 },
          {
            borderColor: theme.palette.brand.white,
            borderWidth: 8,
            dataLabels: {
              formatter: labelFormatter,
              style: { fontSize: '12px', fontWeight: 'regular', textAlign: 'center', textOutline: 'none' },
              useHTML: true
            },
            level: 2
          }
        ],
        type: 'treemap'
      }
    ],
    title: { text: '' },
    tooltip: { enabled: false }
  }

  return (
    <>
      <GlobalStyle />
      <GridContainer id={id}>
        <GridItem md={2} xs={12}>
          <Nav>
            <NavItem onClick={onReset}>
              <NavIcon>{rootIcon}</NavIcon>
              <Typography fontSize="12px" variant="body2">
                {rootName}
              </Typography>
            </NavItem>
            {levels.map((level, index) => (
              <NavItem key={index} onClick={handleClickTree(level, index)}>
                <NavIcon>{level.icon}</NavIcon>
                <Typography fontSize="12px" variant="body2">
                  {level.name}
                </Typography>
              </NavItem>
            ))}
          </Nav>
        </GridItem>
        <GridItem md={10} xs={12}>
          <Highcharts highcharts={Instance} options={options} />
        </GridItem>
      </GridContainer>
    </>
  )
}

TreemapChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    }),
    PropTypes.shape({
      index: PropTypes.number,
      label: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      parent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.number
    })
  ).isRequired,
  id: PropTypes.string.isRequired,
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      name: PropTypes.string
    })
  ).isRequired,
  rootIcon: PropTypes.node.isRequired,
  rootName: PropTypes.string.isRequired,
  labelFormatter: PropTypes.func.isRequired,
  onClickMap: PropTypes.func.isRequired,
  onClickTree: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
}

export default TreemapChart
